import React from 'react';
import classNames from 'classnames';

import { alpha, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { TableRow, TableCell, Box, Checkbox } from '@mui/material';

import { DTAOrderStatuses } from 'common/types';
import useShopFormat from 'hooks/useShopFormat';
import { Routes, push } from 'routing';
import { OrderInfo } from 'common/types';
import { getPricingString } from 'common/utils/common';
import { useTranslation } from 'services/localization/useTranslation';
import { isDTAOrderCanceled, isDTAOrderCompleted } from './utils';
import { getOrderPayments } from 'common/modules/atoms/paymentAmounts';

interface Props {
	order: OrderInfo;
	isChecked: boolean;
	handleRowCheckbox: (orderId: string) => void;
}

const OrderRow = ({ order, handleRowCheckbox, isChecked }: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { localFormat } = useShopFormat();

	const chargePayment = getOrderPayments(order).charge;

	const handleCheckboxClick = (e: React.MouseEvent<unknown>) => {
		e.preventDefault();
		e.stopPropagation();
		handleRowCheckbox(order.id);
	};

	const renderOrderIdCell = () => (
		<TableCell onClick={(e) => e.stopPropagation()}>
			{order.skidataProps?.confirmationNumber}
		</TableCell>
	);

	const renderActivationDateCell = () => (
		<TableCell className={classes.nowrap}>{localFormat(order.startDate, 'DD.MM.YYYY')}</TableCell>
	);

	const renderStatusCell = () => {
		const DTAOrderStatus = order.skidataProps?.DTAOrderStatus;
		const isCanceled = isDTAOrderCanceled(DTAOrderStatus);
		const isCompleted = order.rentalState === 'COMPLETED' || isDTAOrderCompleted(DTAOrderStatus);

		const statusText = isCanceled
			? t('orders.cancelled', 'Cancelled')
			: isCompleted
			? t('orders.completed', 'Completed')
			: t('orders.pending', 'Pending');
		const statusColor = isCanceled
			? classes.canceledStatus
			: isCompleted
			? classes.completedStatus
			: classes.pendingStatus;
		return (
			<TableCell>
				<Box className={classNames(classes.statusCell, statusColor)}>{statusText}</Box>
			</TableCell>
		);
	};

	const renderCustomerCell = () => {
		const shopperNames = order.shopperNames.filter(Boolean);
		const responsiblePerson = order.responsiblePerson.external && order.responsiblePerson.person;
		const customerName = shopperNames.length
			? shopperNames[0]
			: responsiblePerson
			? `${responsiblePerson.firstName}${
					responsiblePerson.lastName && ` ${responsiblePerson.lastName}`
			  }`
			: '';
		return <TableCell data-sl="mask">{customerName}</TableCell>;
	};

	const renderPaymentCell = () => {
		const notPaid = chargePayment.unpaid > 0;

		return (
			<TableCell>
				<Box display="flex" alignItems="center">
					<Box
						className={classNames(
							classes.circle,
							notPaid ? classes.unpaidColor : classes.paidColor,
						)}
					/>
					{notPaid ? t('orders.unpaid', 'Unpaid') : t('orders.paid', 'Paid')}
				</Box>
			</TableCell>
		);
	};

	const renderTotalValueCell = () => (
		<TableCell align="right" className={classes.lastCell}>
			{getPricingString(chargePayment.total)}
		</TableCell>
	);

	const renderOrderCheckbox = () => (
		<Checkbox
			className={classes.orderCheckbox}
			checked={isChecked}
			onClick={handleCheckboxClick}
			inputProps={{ 'aria-label': 'primary checkbox' }}
			disabled={
				!order.skidataProps?.confirmationNumber ||
				order.skidataProps?.DTAOrderStatus === DTAOrderStatuses.CANCELLED
			}
		/>
	);

	return (
		<TableRow className={classes.tableRow} onClick={() => push(Routes.BookingPage(order.id))}>
			{renderOrderCheckbox()}
			{renderOrderIdCell()}
			{renderActivationDateCell()}
			{renderStatusCell()}
			{renderCustomerCell()}
			{renderPaymentCell()}
			{renderTotalValueCell()}
		</TableRow>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tableRow: {
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: theme.palette.action.hover,
			},
		},
		nowrap: {
			whiteSpace: 'nowrap',
		},
		statusCell: {
			padding: theme.spacing(1),
			width: 90,
			borderRadius: 6,
			fontSize: '1.2rem',
			fontWeight: 500,
			textAlign: 'center',
		},
		canceledStatus: {
			color: theme.palette.text.secondary,
			backgroundColor: alpha(theme.palette.text.secondary, 0.2),
		},
		completedStatus: {
			color: theme.palette.success.main,
			backgroundColor: alpha(theme.palette.success.main, 0.2),
		},
		pendingStatus: {
			color: theme.palette.warning.main,
			backgroundColor: alpha(theme.palette.warning.main, 0.2),
		},
		circle: {
			width: 13,
			height: 13,
			borderRadius: '50%',
			marginRight: 10,
			marginBottom: 2,
		},
		paidColor: {
			backgroundColor: theme.palette.success.main,
		},
		unpaidColor: {
			backgroundColor: theme.palette.error.main,
		},
		firstCell: {
			paddingLeft: theme.spacing(5),
		},
		lastCell: {
			paddingRight: theme.spacing(5),
		},
		orderCheckbox: {
			marginTop: theme.spacing(1.5),
			marginLeft: theme.spacing(2),
		},
	}),
);

export default OrderRow;
